import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Bell, BellOff, CheckSquare, FileText, FolderOpen, CalendarDays, Megaphone,
  Check, RefreshCw, Filter, ChevronLeft, ChevronRight,
} from 'lucide-react'
import { apiClient } from '@/services/api'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 20

/** Returns a human-readable time-ago string in Italian */
function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'adesso'
  if (diffMin < 60) return `${diffMin} min fa`
  if (diffHour < 24) return `${diffHour} ${diffHour === 1 ? 'ora' : 'ore'} fa`
  if (diffDay === 1) return 'ieri'
  if (diffDay < 7) return `${diffDay} giorni fa`
  return new Date(dateStr).toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/** Returns the notification link based on type */
function notificationLink(n: Notification): string {
  const type: string = (n.type ?? '').toUpperCase()
  if (type.startsWith('TASK')) return '/tasks'
  if (type.startsWith('PROJECT') && (n.entityId || n.relatedId)) return `/projects/${n.entityId || n.relatedId}`
  if (type.startsWith('CLIENT') && n.relatedId) return `/clients/${n.relatedId}`
  if (type.startsWith('EVENT') && n.relatedId) return `/events/${n.relatedId}`
  if (type.startsWith('CONTENT')) {
    const params = new URLSearchParams()
    if (n.relatedId) params.set('projectId', n.relatedId)
    if (n.entityId) params.set('contentId', n.entityId)
    const qs = params.toString()
    return qs ? `/ped?${qs}` : '/ped'
  }
  if (type.startsWith('QUOTE') || type.startsWith('PREVENTIV')) return '/preventivi'
  if (type.startsWith('MEETING')) return '/meetings'
  return '#'
}

function NotificationTypeIcon({ type }: { type: string }) {
  const t = type ?? ''
  if (t.startsWith('task')) return <CheckSquare className="w-4 h-4" />
  if (t.startsWith('project')) return <FolderOpen className="w-4 h-4" />
  if (t.startsWith('event')) return <CalendarDays className="w-4 h-4" />
  if (t.startsWith('campaign') || t.startsWith('adv')) return <Megaphone className="w-4 h-4" />
  return <FileText className="w-4 h-4" />
}

/** Resolve effective notification type using title as fallback for legacy CONTENT_REVIEW entries */
function resolveNotifType(type: string, title?: string): string {
  const t = (type ?? '').toUpperCase()
  if (t === 'CONTENT_REVIEW' && title && /pronto/i.test(title) && !/approva|revision/i.test(title)) {
    return 'CONTENT_READY'
  }
  return t
}

/** Left border color based on urgency */
function notifBorderColor(type: string, title?: string): string {
  const t = resolveNotifType(type, title)
  // Critical (danger)
  if (t === 'CONTENT_CHANGES_REQUESTED' || t === 'TASK_REOPENED' || t === 'PENDING_CHANGE_REQUEST') return 'border-l-4 border-l-danger-500'
  // Warning
  if (t === 'TASK_ASSIGNED' || t === 'TASK_REASSIGNED' || t === 'TASK_ASSIGNED_NOTIFY' || t === 'CONTENT_REVIEW' || t === 'CONTENT_REWORKED') return 'border-l-4 border-l-warning-500'
  // Success
  if (t === 'CONTENT_READY' || t === 'CONTENT_APPROVED' || t === 'PENDING_CHANGE_APPROVED' || t === 'TASK_STATUS' || t === 'TASK_STATUS_CHANGE') return 'border-l-4 border-l-success-500'
  // Rejected (danger)
  if (t === 'PENDING_CHANGE_REJECTED') return 'border-l-4 border-l-danger-500'
  // Comments (warning — high priority)
  if (t === 'PROJECT_COMMENT' || t === 'TASK_COMMENT' || t === 'TASK_COMMENT_NOTIFY' || t === 'CONTENT_COMMENT') return 'border-l-4 border-l-warning-500'
  // Info (primary)
  return 'border-l-4 border-l-primary-300'
}

function notifIconBg(type: string, title?: string): string {
  const t = resolveNotifType(type, title)
  // Critical — requires immediate action (danger)
  if (t === 'CONTENT_CHANGES_REQUESTED') return 'bg-danger-50 text-danger-600'
  if (t === 'TASK_REOPENED') return 'bg-danger-50 text-danger-600'
  if (t === 'PENDING_CHANGE_REQUEST') return 'bg-danger-50 text-danger-600'
  // Warning — action needed
  if (t === 'TASK_ASSIGNED' || t === 'TASK_REASSIGNED' || t === 'TASK_ASSIGNED_NOTIFY') return 'bg-warning-50 text-warning-600'
  if (t === 'CONTENT_REVIEW' || t === 'CONTENT_REWORKED') return 'bg-warning-50 text-warning-600'
  // Success — positive / informational
  if (t === 'CONTENT_READY') return 'bg-success-50 text-success-600'
  if (t === 'CONTENT_APPROVED') return 'bg-success-50 text-success-600'
  if (t === 'PENDING_CHANGE_APPROVED') return 'bg-success-50 text-success-600'
  if (t === 'TASK_STATUS' || t === 'TASK_STATUS_CHANGE') return 'bg-success-50 text-success-600'
  // Rejected (danger)
  if (t === 'PENDING_CHANGE_REJECTED') return 'bg-danger-50 text-danger-600'
  // Comments (warning — high priority)
  if (t === 'PROJECT_COMMENT' || t === 'TASK_COMMENT' || t === 'TASK_COMMENT_NOTIFY' || t === 'CONTENT_COMMENT') return 'bg-warning-50 text-warning-600'
  // Info — general updates
  if (t.startsWith('PROJECT')) return 'bg-primary-50 text-primary-600'
  if (t.startsWith('CONTENT')) return 'bg-primary-50 text-primary-600'
  if (t.startsWith('TASK')) return 'bg-slate-100 text-slate-500'
  if (t.startsWith('EVENT')) return 'bg-primary-50 text-primary-600'
  if (t.startsWith('CAMPAIGN') || t.startsWith('ADV')) return 'bg-warning-50 text-warning-600'
  return 'bg-primary-50 text-primary-600'
}

interface Notification {
  id: string
  type: string
  title: string
  message?: string
  isRead: boolean
  relatedId?: string
  entityId?: string
  entityType?: string
  createdAt: string
}

type ReadFilter = 'all' | 'unread' | 'read'
type TypeFilter = string

const TYPE_FILTER_OPTIONS: { value: TypeFilter; label: string }[] = [
  { value: 'all', label: 'Tutti i tipi' },
  { value: 'task', label: 'Task' },
  { value: 'project', label: 'Progetti' },
  { value: 'client', label: 'Clienti' },
  { value: 'event', label: 'Eventi' },
  { value: 'campaign', label: 'Campagne' },
]

export function NotificationsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [readFilter, setReadFilter] = useState<ReadFilter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['notifications-page', { page, readFilter, typeFilter }],
    queryFn: () =>
      apiClient.get<any>('/notifications', {
        page,
        limit: PAGE_SIZE,
        isRead: readFilter === 'all' ? undefined : readFilter === 'read' ? true : false,
        type: typeFilter === 'all' ? undefined : typeFilter,
      } as Record<string, unknown>),
    staleTime: 10_000,
    retry: false,
  })

  // Handle multiple possible response shapes:
  // 1. Unwrapped paginated: { items: [...], total, page, ... }
  // 2. Non-unwrapped with data array: { data: [...], total, ... }
  // 3. Non-unwrapped nested: { data: { items: [...], total, ... } }
  // 4. Plain array: [...]
  const raw = data as any
  const notificationsRaw: unknown =
    raw?.items ?? raw?.data?.items ?? raw?.data?.data ?? raw?.data ?? raw ?? []
  const notifications: Notification[] = Array.isArray(notificationsRaw) ? notificationsRaw : []
  const total: number = raw?.total ?? raw?.data?.total ?? raw?.pagination?.total ?? notifications.length
  const totalPages = Math.ceil(total / PAGE_SIZE) || 1

  const markReadMutation = useMutation({
    mutationFn: (payload: { ids?: string[]; all?: boolean }) =>
      apiClient.post('/notifications/mark-read', payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications-page'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] })
      void queryClient.invalidateQueries({ queryKey: ['notifications-recent'] })
    },
  })

  function handleMarkOne(id: string) {
    markReadMutation.mutate({ ids: [id] })
  }

  function handleMarkAll() {
    markReadMutation.mutate({ all: true })
  }

  function handleNotifClick(n: Notification) {
    if (!n.isRead) {
      markReadMutation.mutate({ ids: [n.id] })
    }
    const link = notificationLink(n)
    if (link !== '#') navigate(link)
  }

  const unreadCount = notifications.filter((n) => !n.isRead).length

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
            <Bell className="w-5 h-5 text-primary-600" />
            Notifiche
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {total > 0
              ? `${total} notifiche totali`
              : 'Nessuna notifica'}
            {unreadCount > 0 && `, ${unreadCount} non lette`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => void refetch()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" />
            Aggiorna
          </button>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAll}
              disabled={markReadMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
            >
              <Check className="w-3.5 h-3.5" />
              Segna tutto come letto
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Read filter tabs */}
        <div className="flex items-center border border-slate-200 rounded-lg overflow-hidden bg-white">
          {(['all', 'unread', 'read'] as ReadFilter[]).map((f) => (
            <button
              key={f}
              onClick={() => { setReadFilter(f); setPage(1) }}
              className={cn(
                'px-3 py-1.5 text-xs font-medium transition-colors',
                readFilter === f
                  ? 'bg-primary-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              )}
            >
              {f === 'all' ? 'Tutte' : f === 'unread' ? 'Non lette' : 'Lette'}
            </button>
          ))}
        </div>

        {/* Type filter */}
        <div className="flex items-center gap-1.5">
          <Filter className="w-4 h-4 text-slate-400" />
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1) }}
            className="px-2 py-1.5 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 text-slate-700"
          >
            {TYPE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Notifications list */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
        {/* Loading skeleton */}
        {isLoading && (
          <div className="divide-y divide-slate-200">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="flex items-start gap-4 px-5 py-4">
                <div className="w-10 h-10 bg-slate-100 rounded-xl animate-pulse flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3.5 bg-slate-100 rounded animate-pulse w-48" />
                  <div className="h-3 bg-slate-100 rounded animate-pulse w-72" />
                  <div className="h-2.5 bg-slate-100 rounded animate-pulse w-24" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Error */}
        {isError && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-12 h-12 bg-danger-50 rounded-full flex items-center justify-center mb-3">
              <BellOff className="w-6 h-6 text-danger-400" />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-1">Errore nel caricamento</p>
            <p className="text-xs text-slate-400">Impossibile caricare le notifiche.</p>
            <button
              onClick={() => void refetch()}
              className="mt-3 text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Riprova
            </button>
          </div>
        )}

        {/* Empty state */}
        {!isLoading && !isError && notifications.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-14 h-14 bg-slate-100 rounded-full flex items-center justify-center mb-4">
              <BellOff className="w-7 h-7 text-slate-300" />
            </div>
            <h3 className="text-sm font-medium text-slate-700 mb-1">
              {readFilter === 'unread'
                ? 'Nessuna notifica non letta'
                : readFilter === 'read'
                ? 'Nessuna notifica letta'
                : 'Nessuna notifica'}
            </h3>
            <p className="text-xs text-slate-400">
              {readFilter === 'all'
                ? 'Le notifiche appariranno qui quando ci saranno aggiornamenti.'
                : 'Prova a cambiare i filtri.'}
            </p>
          </div>
        )}

        {/* Notifications */}
        {!isLoading && !isError && notifications.length > 0 && (
          <div className="divide-y divide-slate-200">
            {notifications.map((n) => (
              <div
                key={n.id}
                className={cn(
                  'flex items-start gap-3 px-3 sm:px-5 py-3 sm:py-4 transition-colors',
                  notificationLink(n) !== '#'
                    ? 'cursor-pointer hover:bg-slate-50'
                    : '',
                  !n.isRead && 'bg-primary-50/40',
                  notifBorderColor(n.type, n.title)
                )}
                onClick={() => handleNotifClick(n)}
              >
                {/* Type icon */}
                <div className={cn(
                  'flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center',
                  notifIconBg(n.type, n.title)
                )}>
                  <NotificationTypeIcon type={n.type} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={cn(
                        'text-sm leading-snug',
                        !n.isRead ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'
                      )}>
                        {n.title}
                      </p>
                      {n.message && (
                        <p className="text-sm text-slate-500 mt-0.5 line-clamp-2 leading-snug">
                          {n.message}
                        </p>
                      )}
                      <p className="text-xs text-slate-400 mt-1.5 tabular-nums">{timeAgo(n.createdAt)}</p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!n.isRead && (
                        <>
                          <span className="w-2 h-2 bg-primary-500 rounded-full" />
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleMarkOne(n.id)
                            }}
                            disabled={markReadMutation.isPending}
                            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
                            title="Segna come letto"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-slate-400 tabular-nums">
            Pagina {page} di {totalPages} ({total} notifiche)
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="flex items-center gap-1 px-4 py-2.5 text-sm text-slate-700 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
              Precedente
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="flex items-center gap-1 px-4 py-2.5 text-sm text-slate-700 border border-slate-200 rounded-lg bg-white hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Successiva
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
